import * as moment from 'moment';
import * as luhn from 'luhn';
import { isString } from 'lodash';

type ValidatorReturn = string | null;
type DateInput = string | moment.Moment;

const DATE_FORMAT = 'DD/MM/YYYY';

const isRequired = (value): ValidatorReturn => {
  // An exact false is an answered question
  // checking something isn't false should be another validator
  if (value === false) {
    return null;
  }

  return !value || value.length === 0 ? 'This field is required.' : null;
};

const isNotFalse = (value): ValidatorReturn => {
  if (value) {
    return null;
  }

  return 'This field is required.';
};

const parseDate = (date: DateInput): moment.Moment => {
  if (!isString(date)) {
    return date;
  }

  return moment(date, DATE_FORMAT);
};

const isValidDate = (value: string): ValidatorReturn => {
  return !parseDate(value).isValid() ? 'Invalid Date' : null;
};

const isBefore = (date: DateInput, message: string = null) => (value: string): ValidatorReturn => {
  if (!date) {
    return null;
  }

  date = parseDate(date);
  const m = message ? message : 'Must be before ' + date.format(DATE_FORMAT);
  const momentValue = parseDate(value);

  return date.isBefore(momentValue) ? m : null;
};

const isAfter = (date: DateInput, message: string = null) => (value: string): ValidatorReturn => {
  date = parseDate(date);
  const m = message ? message : 'Must be after ' + date.format(DATE_FORMAT);
  const momentValue = parseDate(value);

  return date.isAfter(momentValue) ? m : null;
};

const isFutureDate = (value: string) => {
  const date = moment().startOf('day');
  const momentValue = parseDate(value);

  return date.isSame(momentValue) ? null : isAfter(date, 'Date cannot be in the past')(value);
};

const isAdult = isBefore(moment().startOf('day').subtract(18, 'years'), 'Traveller must be an adult');

// tslint:disable:max-line-length
// Regex from http://emailregex.com/
const emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const isEmail = (value: string): ValidatorReturn => {
  return !emailRegex.test(value) ? 'Invalid Email' : null;
};

const phoneRegex = /^[+]?[0-9]+$/;

const isPhone = (value: string): ValidatorReturn => {
  return !phoneRegex.test(value) ? 'Invalid phone number' : null;
};

const isLuhnHappy = (message = 'This does not look like a valid card number') => (value: string): ValidatorReturn => {
  const val = value.replace(/\s/g, '');

  return luhn.validate(val) ? null : message;
};

const isLengthBetween = ([ min, max ]: [ number, number ],
                         message = 'This value should be between %s and %s characters.') =>
  (value: string): ValidatorReturn => {
    const length = value.length;

    return length >= min && length <= max ? null : message.replace(/%s/i, [ min, max ].join(', '));
  };

export {
  DATE_FORMAT,
  isRequired,
  isValidDate,
  isFutureDate,
  isAdult,
  isAfter,
  isBefore,
  isEmail,
  isPhone,
  isNotFalse,
  isLuhnHappy,
  isLengthBetween,
};
